/**
 * Archive Cascade Utilities
 *
 * Handles cascading archive/restore operations for client-related entities.
 * When a client is archived, related contracts, agreements, projects, and goals
 * are also archived. When restored, only items that were cascade-archived are restored.
 */

import { supabase } from '@/integrations/supabase/client';
import { todayString } from '@/lib/date-utils';

/**
 * Cascade archive operations to related entities when a client is archived.
 * Only archives entities that are not already archived.
 */
export async function cascadeArchiveClient(clientId: string): Promise<void> {
  const { error: contractsError } = await supabase
    .from('contractual_documents')
    .update({ is_archived: true, archived_by_client: true })
    .eq('client_id', clientId)
    .eq('is_archived', false);
  if (contractsError) {
    console.error('Failed to cascade archive to contracts:', contractsError);
  }

  const { error: agreementsError } = await supabase
    .from('client_agreements')
    .update({ is_archived: true, archived_by_client: true })
    .eq('client_id', clientId)
    .eq('is_archived', false);
  if (agreementsError) {
    console.error('Failed to cascade archive to agreements:', agreementsError);
  }

  const { error: projectsError } = await supabase
    .from('projects')
    .update({ is_archived: true, archived_by_client: true })
    .eq('client_id', clientId)
    .eq('is_archived', false);
  if (projectsError) {
    console.error('Failed to cascade archive to projects:', projectsError);
  }

  const { error: goalsError } = await supabase
    .from('client_goals')
    .update({ is_archived: true, archived_by_client: true })
    .eq('client_id', clientId)
    .eq('is_archived', false);
  if (goalsError) {
    console.error('Failed to cascade archive to goals:', goalsError);
  }

  // Set end_date on financial settings so budget calculations exclude this client.
  // Only set if end_date is currently null (don't overwrite a manually-set date).
  const today = todayString();
  const { error: financialError } = await supabase
    .from('client_financial_settings')
    .update({ end_date: today })
    .eq('client_id', clientId)
    .is('end_date', null);
  if (financialError) {
    console.error('Failed to set end_date on financial settings:', financialError);
  }
}

/**
 * Cascade restore operations to related entities when a client is restored.
 * Only restores entities that were cascade-archived (archived_by_client = true).
 */
export async function cascadeRestoreClient(clientId: string): Promise<void> {
  const { error: contractsError } = await supabase
    .from('contractual_documents')
    .update({ is_archived: false, archived_by_client: false })
    .eq('client_id', clientId)
    .eq('archived_by_client', true);
  if (contractsError) {
    console.error('Failed to cascade restore contracts:', contractsError);
  }

  const { error: agreementsError } = await supabase
    .from('client_agreements')
    .update({ is_archived: false, archived_by_client: false })
    .eq('client_id', clientId)
    .eq('archived_by_client', true);
  if (agreementsError) {
    console.error('Failed to cascade restore agreements:', agreementsError);
  }

  const { error: projectsError } = await supabase
    .from('projects')
    .update({ is_archived: false, archived_by_client: false })
    .eq('client_id', clientId)
    .eq('archived_by_client', true);
  if (projectsError) {
    console.error('Failed to cascade restore projects:', projectsError);
  }

  const { error: goalsError } = await supabase
    .from('client_goals')
    .update({ is_archived: false, archived_by_client: false })
    .eq('client_id', clientId)
    .eq('archived_by_client', true);
  if (goalsError) {
    console.error('Failed to cascade restore goals:', goalsError);
  }

  // Clear end_date on financial settings so budget calculations include this client again.
  // Only clear if end_date equals today (was set by the archive cascade, not manually).
  const today = todayString();
  const { error: financialError } = await supabase
    .from('client_financial_settings')
    .update({ end_date: null })
    .eq('client_id', clientId)
    .eq('end_date', today);
  if (financialError) {
    console.error('Failed to clear end_date on financial settings:', financialError);
  }
}
